import {
  Autocomplete,
  Chip,
  type ChipProps,
  FormHelperText,
  TextField,
  Typography,
} from '@mui/material'
import { type SyntheticEvent, useMemo, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  selectDisabledOptionIdsByFieldName,
  selectField,
} from '../../selectors'
import { setValue } from '../../slice'
import type { FieldName, MultiSelectValue, SelectOption } from '../../types'
import fieldSchemas from '../../validation'
import OptionWarning from '../OptionWarning'

type MultiSelectProps = {
  name: FieldName
}

export default function MultiSelect(props: MultiSelectProps) {
  const { name } = props

  const { field, value, options = [] } = useAppSelector(selectField(name))
  const disabledOptionIds = useAppSelector(
    selectDisabledOptionIdsByFieldName(name),
  )
  const dispatch = useAppDispatch()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const { name: fieldName, label } = field

  const isRequiredCharacter =
    field.validations && field?.validations?.find((v) => v.type === 'required')
      ? '*'
      : ''

  const sortedOptions = useMemo(
    () =>
      [...options].sort((a: SelectOption, b: SelectOption) => {
        const isADisabled = disabledOptionIds?.includes(a?.code) ?? false
        const isBDisabled = disabledOptionIds?.includes(b?.code) ?? false

        if (isADisabled > isBDisabled) return 1
        if (isADisabled < isBDisabled) return -1
        return 0
      }),
    [options, disabledOptionIds],
  )

  function renderTags(
    value: SelectOption[],
    getTagProps: ({ index }: { index: number }) => ChipProps,
  ) {
    return value.map((option: SelectOption, index: number) => {
      const issue = disabledOptionIds?.includes(option.code)
      return (
        <CustomChip option={option} issue={issue} {...getTagProps({ index })} />
      )
    })
  }

  function handleChange(_event: SyntheticEvent, value: MultiSelectValue) {
    function validate() {
      setErrorMessages([])
      const errors: string[] = []
      const validationFunctions =
        fieldSchemas[name]?.map((func) => func.validate(value)) || []

      Promise.all(validationFunctions)
        .then(() => {
          dispatch(setValue({ fieldName, value }))
        })
        .catch((e) => {
          errors.push(e.message)
          dispatch(setValue({ fieldName, value }))
        })

      setErrorMessages(errors)
    }
    validate()
  }

  return (
    <>
      <Autocomplete
        key={name}
        {...{
          name,
          label,
          value: value as MultiSelectValue,
          options: sortedOptions,
        }}
        loading={!options || !options.length}
        multiple={true}
        renderTags={renderTags}
        isOptionEqualToValue={(option, value) => option.code === value.code}
        getOptionLabel={(option) => option.description}
        getOptionDisabled={(option) =>
          disabledOptionIds?.includes(option.code) || false
        }
        onChange={handleChange}
        renderInput={(params) => (
          <TextField label={label + isRequiredCharacter} {...params} />
        )}
      />
      <Typography variant="caption">Select multiple options.</Typography>
      <FormHelperText>{errorMessages}</FormHelperText>
      <OptionWarning fieldName={name} />
    </>
  )
}

function CustomChip({ option, issue, ...props }: TODO) {
  let style = {
    color: 'default',
    variant: 'filled',
  }

  if (issue) {
    style = { color: 'error', variant: 'outlined' }
  }

  return <Chip {...style} label={option.description || 'none'} {...props} />
}
